import { ArrowUpRight, Check, ChevronDown, ListChecks, MoreHorizontal, Video } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ExerciseMedia, safeVideoUrl } from '../../components/ExerciseMedia'
import { NumberControl } from '../../components/NumberControl'
import { useStrata } from '../../data/StrataProvider'
import { lastExerciseSets, progressionSuggestion } from '../../domain/progression'
import { overloadCycle } from '../../domain/overload'
import type { Exercise, RoutineExercise, SetRecord, WorkoutSession, WorkoutTemplate } from '../../domain/types'
import { displayMass, formatMass, formatTrainingVolume, massStep, massUnit, storeMass, type UnitSystem } from '../../domain/units'

export function ActiveWorkout({ template, onClose }: { template: WorkoutTemplate; onClose: () => void }) {
  const { state, upsertSession } = useStrata()
  const resumedSession = useRef([...state.sessions].reverse().find((session) => session.templateId === template.id && session.status === 'in_progress')).current
  const resumedCursor = useRef(workoutCursor(template, resumedSession?.sets ?? [])).current
  const startedAt = useRef(new Date(resumedSession?.startedAt ?? Date.now()))
  const sessionId = useRef(resumedSession?.id ?? crypto.randomUUID())
  const [exerciseIndex, setExerciseIndex] = useState(resumedCursor.exerciseIndex)
  const [setIndex, setSetIndex] = useState(resumedCursor.setIndex)
  const [records, setRecords] = useState<SetRecord[]>(resumedSession?.sets ?? [])
  const [weight, setWeight] = useState(0)
  const [reps, setReps] = useState(8)
  const [duration, setDuration] = useState(0)
  const [assistance, setAssistance] = useState(0)
  const [band, setBand] = useState('Ninguna')
  const [success, setSuccess] = useState(100)
  const [technique, setTechnique] = useState('Estricto')
  const [effortMode, setEffortMode] = useState<'RIR' | 'RPE'>('RIR')
  const [effort, setEffort] = useState(2)
  const [note, setNote] = useState('')
  const [restUntil, setRestUntil] = useState<number>()
  const [restLabel, setRestLabel] = useState('')
  const [restTotal, setRestTotal] = useState(0)
  const [remaining, setRemaining] = useState(0)
  const [summary, setSummary] = useState<WorkoutSession>()
  const [elapsed, setElapsed] = useState(0)
  const initializedExercise = useRef('')

  const entry = template.entries[exerciseIndex]
  const exercise = state.exercises.find((item) => item.id === entry.exerciseId)!
  const previous = useMemo(() => lastExerciseSets(state.sessions, exercise.id, state.preferences.loadMemoryWeeks), [state.sessions, state.preferences.loadMemoryWeeks, exercise.id])
  const suggestion = useMemo(() => progressionSuggestion(exercise, entry, previous), [exercise, entry, previous])
  const overload = overloadCycle(state.profile, state.preferences)

  useEffect(() => {
    const key = `${exerciseIndex}:${exercise.id}`
    if (initializedExercise.current === key) return
    initializedExercise.current = key
    const draftSets = records.filter((record) => record.exerciseId === exercise.id).sort((a, b) => a.setNumber - b.setNumber)
    const saved = draftSets.find((record) => record.setNumber === setIndex + 1) ?? draftSets.at(-1)
    const bestWeight = Math.max(0, ...previous.map((set) => set.weight ?? 0))
    if (saved) {
      setWeight(saved.weight ?? 0)
      setReps(saved.reps ?? entry.minReps)
    } else if (exercise.preferredWeight !== undefined) {
      setWeight(exercise.preferredWeight)
      setReps(previous[0]?.reps ?? entry.minReps)
    } else if (suggestion?.kind === 'load') {
      setWeight(suggestion.weight)
      setReps(suggestion.reps)
    } else {
      setWeight(bestWeight)
      setReps(previous[0]?.reps ?? entry.minReps)
    }
    const source = saved ?? previous[0]
    setDuration(exercise.category === 'HYROX' ? (source?.durationSeconds ?? entry.minReps * 60) / 60 : source?.durationSeconds ?? 0)
    setAssistance(source?.assistance ?? 0)
    setBand(source?.band ?? 'Ninguna')
    setSuccess(source?.success ?? 100)
    setTechnique(source?.technique ?? 'Estricto')
    setEffort(source?.rir ?? source?.rpe ?? targetRirValue(entry.targetRir))
    setEffortMode(source?.rpe !== undefined ? 'RPE' : 'RIR')
    setNote(saved?.note ?? '')
  }, [exerciseIndex, exercise.id, entry.minReps, entry.targetRir, exercise.category, previous, suggestion, records, setIndex])

  useEffect(() => {
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt.current.getTime()) / 1000)), 1000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!restUntil) return
    const update = () => {
      const seconds = Math.max(0, Math.ceil((restUntil - Date.now()) / 1000))
      setRemaining(seconds)
      if (!seconds) {
        setRestUntil(undefined)
        navigator.vibrate?.([100, 60, 100])
        if (state.preferences.restSound) playRestSound()
      }
    }
    update()
    const timer = window.setInterval(update, 250)
    return () => window.clearInterval(timer)
  }, [restUntil, state.preferences.restSound])

  const sessionFor = useCallback((finishedRecords: SetRecord[], status: 'in_progress' | 'completed'): WorkoutSession => {
    const ended = new Date()
    const historical = state.sessions.filter((session) => session.id !== sessionId.current).flatMap((session) => session.sets)
    return {
      id: sessionId.current,
      templateId: template.id,
      name: template.name,
      startedAt: startedAt.current.toISOString(),
      endedAt: ended.toISOString(),
      sets: finishedRecords,
      volume: finishedRecords.reduce((sum, set) => sum + (set.weight ?? 0) * (set.reps ?? 0), 0),
      prCount: status === 'completed' ? finishedRecords.filter((set) => isRecord(set, historical)).length : 0,
      status,
    }
  }, [state.sessions, template.id, template.name])

  const finish = useCallback((finishedRecords: SetRecord[]) => {
    const session = sessionFor(finishedRecords, 'completed')
    upsertSession(session)
    setRestUntil(undefined)
    setSummary(session)
  }, [sessionFor, upsertSession])

  const selectSet = (nextIndex: number) => {
    setRestUntil(undefined)
    setSetIndex(nextIndex)
    const exerciseRecords = records.filter((record) => record.exerciseId === exercise.id)
    const saved = exerciseRecords.find((record) => record.setNumber === nextIndex + 1)
    const latest = [...exerciseRecords].sort((a, b) => b.completedAt.localeCompare(a.completedAt))[0]
    const source = saved ?? latest ?? previous[nextIndex] ?? previous[0]
    if (!source) return
    setWeight(source.weight ?? weight)
    setReps(source.reps ?? reps)
    setDuration(exercise.category === 'HYROX' ? (source.durationSeconds ?? duration * 60) / 60 : source.durationSeconds ?? duration)
    setAssistance(source.assistance ?? assistance)
    setBand(source.band ?? band)
    setSuccess(source.success ?? success)
    setTechnique(source.technique ?? technique)
    setEffort(source.rir ?? source.rpe ?? effort)
    setEffortMode(source.rpe !== undefined ? 'RPE' : 'RIR')
    setNote(saved?.note ?? '')
  }

  const selectExercise = (nextExerciseIndex: number) => {
    setRestUntil(undefined)
    const nextEntry = template.entries[nextExerciseIndex]
    const nextSet = Array.from({ length: nextEntry.sets }, (_, index) => index).find((index) => !records.some((record) => record.exerciseId === nextEntry.exerciseId && record.setNumber === index + 1)) ?? 0
    if (nextExerciseIndex === exerciseIndex) {
      selectSet(nextSet)
      return
    }
    initializedExercise.current = ''
    setExerciseIndex(nextExerciseIndex)
    setSetIndex(nextSet)
  }

  const completeSet = () => {
    const existing = records.find((record) => record.exerciseId === exercise.id && record.setNumber === setIndex + 1)
    const completed: SetRecord = {
      id: existing?.id ?? crypto.randomUUID(),
      exerciseId: exercise.id,
      exerciseName: exercise.name,
      position: exerciseIndex,
      setNumber: setIndex + 1,
      reps: exercise.metrics.includes('reps') ? reps : undefined,
      weight: exercise.metrics.includes('weight') ? weight : undefined,
      durationSeconds: exercise.metrics.includes('duration') && duration > 0 ? duration * (exercise.category === 'HYROX' ? 60 : 1) : undefined,
      [effortMode.toLowerCase()]: effort,
      assistance: exercise.metrics.includes('assistance') ? assistance : undefined,
      band: band === 'Ninguna' ? undefined : band,
      technique: exercise.metrics.includes('technique') ? technique : undefined,
      success: exercise.metrics.includes('success') ? success : undefined,
      note: note.trim() || undefined,
      completedAt: new Date().toISOString(),
    }
    const nextRecords = existing
      ? records.map((record) => record.id === existing.id ? completed : record)
      : [...records, completed]
    setRecords(nextRecords)
    navigator.vibrate?.(25)

    const nextCursor = incompleteWorkoutCursor(template, nextRecords)
    if (!nextCursor) {
      finish(nextRecords)
      return
    }

    upsertSession(sessionFor(nextRecords, 'in_progress'))

    setRestLabel(`${exercise.name} · Serie ${setIndex + 1}`)
    setExerciseIndex(nextCursor.exerciseIndex)
    setSetIndex(nextCursor.setIndex)
    setRestTotal(entry.restSeconds)
    setRestUntil(Date.now() + entry.restSeconds * 1000)
  }

  const progress = (() => {
    const total = template.entries.reduce((sum, item) => sum + item.sets, 0)
    return (records.length / total) * 100
  })()
  const totalSets = template.entries.reduce((sum, item) => sum + item.sets, 0)

  if (summary) return <WorkoutSummary session={summary} unit={state.preferences.unit} onClose={onClose} />

  return (
    <div className="active-workout" role="dialog" aria-modal="true" aria-label={`Entrenamiento ${template.name}`}>
      <header className="workout-toolbar">
        <button className="icon-button" onClick={() => records.length ? onClose() : window.confirm('¿Cerrar este entrenamiento sin completar series?') && onClose()} aria-label="Cerrar"><ChevronDown /></button>
        <strong>{template.name}</strong>
        <span>{clock(elapsed)}</span>
      </header>

      <div className="workout-progress"><i style={{ width: `${progress}%` }} /></div>
      <div className="workout-meta-strip"><span>{records.length} / {totalSets} series</span><span>{records.length ? 'Progreso autoguardado' : 'Descanso automático'}</span></div>
      {overload.due && <div className="overload-workout-alert"><ArrowUpRight size={15} /><span><strong>Semana de sobrecarga</strong> Sube sólo si completas el rango con técnica y RIR objetivo.</span></div>}

      <div className="workout-scroll" key={exercise.id}>
        <section className="active-heading">
          <div>
            <span className="eyebrow">{String(exerciseIndex + 1).padStart(2, '0')} / {String(template.entries.length).padStart(2, '0')}</span>
            <h1>{exercise.name.toUpperCase()}</h1>
            <p>Serie {setIndex + 1} de {entry.sets} · objetivo RIR {entry.targetRir}</p>
          </div>
          <ExerciseMedia exercise={exercise} size={64} />
        </section>

        <div className="set-counter" aria-label={`Serie ${setIndex + 1} de ${entry.sets}`}>
          {Array.from({ length: entry.sets }, (_, index) => {
            const saved = records.some((record) => record.exerciseId === exercise.id && record.setNumber === index + 1)
            return <button key={index} className={`${saved ? 'done' : ''} ${index === setIndex ? 'current' : ''}`} onClick={() => selectSet(index)} aria-label={`${saved ? 'Editar' : 'Ir a'} serie ${index + 1}`}><span>{index + 1}</span></button>
          })}
        </div>

        <section className="set-controls">
          {exercise.metrics.includes('weight') && <NumberControl label={exercise.category === 'Anillas' ? 'Lastre' : 'Peso'} unit={massUnit(state.preferences.unit)} value={displayMass(weight, state.preferences.unit)} step={massStep(exercise.increment, state.preferences.unit)} decimals={2} onChange={(value) => setWeight(storeMass(value, state.preferences.unit))} />}
          {exercise.metrics.includes('weight') && exercise.metrics.includes('reps') && <hr />}
          {exercise.metrics.includes('reps') && <NumberControl label="Repeticiones" unit="reps" value={reps} step={1} onChange={setReps} />}
          {exercise.metrics.includes('duration') && !exercise.metrics.includes('reps') && <NumberControl label="Duración" unit={exercise.category === 'HYROX' ? 'min' : 's'} value={duration} step={exercise.category === 'HYROX' ? 1 : 5} onChange={setDuration} />}
        </section>

        <section className="comparison">
          <div><span>Última vez</span><strong>{previousLabel(previous, state.preferences.unit)}</strong></div>
          {suggestion && <div><span>Sugerencia</span><b><ArrowUpRight size={14} /> {suggestionLabel(suggestion, state.preferences.unit)}</b></div>}
        </section>

        <details className="remaining-exercises">
          <summary><span><ListChecks size={17} /> Ver rutina completa</span><small>Toca para ir o volver</small></summary>
          <div>
            {template.entries.map((remainingEntry, index) => {
              const remainingExercise = state.exercises.find((item) => item.id === remainingEntry.exerciseId)!
              const completed = records.filter((record) => record.exerciseId === remainingEntry.exerciseId).length
              return <button key={remainingEntry.exerciseId} className={index === exerciseIndex ? 'current' : ''} onClick={() => selectExercise(index)}><i>{index + 1}</i><span><strong>{remainingExercise.name}</strong><small>{completed}/{remainingEntry.sets} series · {remainingEntry.minReps}–{remainingEntry.maxReps} reps</small></span>{index === exerciseIndex ? <em>AHORA</em> : completed === remainingEntry.sets ? <em>LISTO</em> : null}</button>
            })}
          </div>
        </details>

        <div className="technique-cue"><span>CLAVE</span><p>{entry.cue}</p></div>
        {safeVideoUrl(exercise.videoUrl) && <a className="active-video-link" href={safeVideoUrl(exercise.videoUrl)} target="_blank" rel="noreferrer"><Video size={15} /> Ver video de técnica</a>}

        <details className="workout-details">
          <summary><span>RIR, técnica y notas</span><MoreHorizontal size={18} /></summary>
          <div className="details-content">
            <div className="effort-row">
              <div className="segmented compact">
                <button className={effortMode === 'RIR' ? 'active' : ''} onClick={() => setEffortMode('RIR')}>RIR</button>
                <button className={effortMode === 'RPE' ? 'active' : ''} onClick={() => setEffortMode('RPE')}>RPE</button>
              </div>
              <label>{effortMode} <input type="number" min="0" max="10" step="0.5" value={effort} onChange={(event) => setEffort(Number(event.target.value))} /></label>
            </div>
            {exercise.metrics.includes('assistance') && <NumberControl label="Asistencia" unit={massUnit(state.preferences.unit)} value={displayMass(assistance, state.preferences.unit)} step={massStep(exercise.increment, state.preferences.unit)} decimals={2} onChange={(value) => setAssistance(storeMass(value, state.preferences.unit))} />}
            {exercise.metrics.includes('assistance') && <div className="segmented four">{['Ninguna', 'Ligera', 'Media', 'Fuerte'].map((item) => <button key={item} className={band === item ? 'active' : ''} onClick={() => setBand(item)}>{item}</button>)}</div>}
            {exercise.metrics.includes('duration') && exercise.metrics.includes('reps') && <NumberControl label="Hold / duración" unit="s" value={duration} step={5} onChange={setDuration} />}
            {exercise.metrics.includes('success') && <label className="range-label"><span>Éxito <b>{success}%</b></span><input type="range" min="0" max="100" step="5" value={success} onChange={(event) => setSuccess(Number(event.target.value))} /></label>}
            {exercise.metrics.includes('technique') && <label className="select-label">Técnica<select value={technique} onChange={(event) => setTechnique(event.target.value)}><option>Estricto</option><option>Transición</option><option>Kipping</option></select></label>}
            <textarea placeholder="Nota opcional" value={note} onChange={(event) => setNote(event.target.value)} />
          </div>
        </details>
      </div>

      <footer className="workout-bottom">
        {restUntil ? (
          <div className="rest-panel">
            <div className="rest-copy"><span className="eyebrow rest">DESCANSO AUTOMÁTICO</span><small>{restLabel}</small></div>
            <div className="rest-countdown" style={{ '--rest-angle': `${restTotal ? Math.min(360, (remaining / restTotal) * 360) : 0}deg` } as React.CSSProperties}><strong>{clock(remaining)}</strong><span>SIGUIENTE SERIE</span></div>
            <div className="rest-actions">
              <button onClick={() => { setRestTotal((value) => Math.max(15, value - 15)); setRestUntil((value) => (value ?? Date.now()) - 15_000) }}>−15</button>
              <button className="dark" onClick={() => setRestUntil(undefined)}>Omitir</button>
              <button onClick={() => { setRestTotal((value) => value + 15); setRestUntil((value) => (value ?? Date.now()) + 15_000) }}>+15</button>
            </div>
          </div>
        ) : (
          <button className="primary-button" onClick={completeSet}><Check size={19} />{records.some((record) => record.exerciseId === exercise.id && record.setNumber === setIndex + 1) ? 'Actualizar serie' : 'Completar serie'}</button>
        )}
      </footer>
    </div>
  )
}

function WorkoutSummary({ session, unit, onClose }: { session: WorkoutSession; unit: UnitSystem; onClose: () => void }) {
  const seconds = Math.max(0, Math.round((new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 1000))
  return (
    <div className="active-workout workout-summary">
      <div className="summary-check"><Check size={32} /></div>
      <span className="eyebrow">ENTRENAMIENTO GUARDADO</span>
      <h1>Sesión completada</h1>
      <p>{session.name}</p>
      <div className="summary-metrics">
        <div><strong>{clock(seconds)}</strong><span>TIEMPO</span></div>
        <div><strong>{formatTrainingVolume(session.volume, unit)}</strong><span>VOLUMEN</span></div>
        <div><strong>{session.prCount}</strong><span>PR</span></div>
      </div>
      <button className="primary-button" onClick={onClose}>Listo</button>
    </div>
  )
}

function previousLabel(sets: SetRecord[], unit: UnitSystem) {
  if (!sets.length) return 'Primera vez'
  const best = [...sets].sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0) || (b.reps ?? 0) - (a.reps ?? 0))[0]
  if (best.durationSeconds) return clock(best.durationSeconds)
  if (best.weight) return `${formatMass(best.weight, unit)} × ${best.reps ?? 0}`
  return `${best.reps ?? 0} reps`
}

function isRecord(set: SetRecord, history: SetRecord[]) {
  const comparable = history.filter((item) => item.exerciseId === set.exerciseId)
  if ((set.weight ?? 0) > Math.max(0, ...comparable.map((item) => item.weight ?? 0))) return true
  return (set.reps ?? 0) > Math.max(0, ...comparable.map((item) => item.reps ?? 0))
}

function playRestSound() {
  try {
    const Context = window.AudioContext
    const context = new Context()
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.frequency.value = 620
    gain.gain.setValueAtTime(.08, context.currentTime)
    gain.gain.exponentialRampToValueAtTime(.001, context.currentTime + .35)
    oscillator.connect(gain).connect(context.destination)
    oscillator.start()
    oscillator.stop(context.currentTime + .35)
  } catch { /* El estado visual sigue funcionando si el navegador bloquea audio. */ }
}

function clock(total: number) {
  const value = Math.max(0, Math.floor(total))
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`
}

function targetRirValue(target: string) {
  const values = target.match(/\d+(?:\.\d+)?/g)?.map(Number)
  return values?.at(-1) ?? 2
}

function suggestionLabel(suggestion: ReturnType<typeof progressionSuggestion>, unit: UnitSystem) {
  if (!suggestion) return ''
  if (suggestion.kind === 'load') return `Sube a ${formatMass(suggestion.weight, unit)}`
  if (suggestion.kind === 'assistance') return `Ayuda ${formatMass(suggestion.assistance, unit)}`
  return suggestion.label
}

function workoutCursor(template: WorkoutTemplate, records: SetRecord[]) {
  return incompleteWorkoutCursor(template, records) ?? {
    exerciseIndex: Math.max(0, template.entries.length - 1),
    setIndex: Math.max(0, template.entries.at(-1)!.sets - 1),
  }
}

function incompleteWorkoutCursor(template: WorkoutTemplate, records: SetRecord[]) {
  for (let exerciseIndex = 0; exerciseIndex < template.entries.length; exerciseIndex += 1) {
    const entry = template.entries[exerciseIndex]
    for (let setIndex = 0; setIndex < entry.sets; setIndex += 1) {
      if (!records.some((record) => record.exerciseId === entry.exerciseId && record.setNumber === setIndex + 1)) return { exerciseIndex, setIndex }
    }
  }
  return undefined
}
